import {useState} from 'react';
import {useWorkspace} from '../hooks/useWorkspace';
import {dataClient,getBotSoftLimit} from '../data/dataClient';
import {createBot,cloneBot,createCustomTeam,sendChat} from '../services/api';
import {BotCard} from '../components/BotComponents';
import ChatInput from '../components/ChatInput';
import {ModelTierSelector,ProviderSelector} from '../components/Selectors';
import {RosterTransferPanel} from '../components/RosterTransferPanel';

// BB-1 (docs/build_bot_platform.md §4): bot/team creation, cloning, custom
// teams. Team creation enforces the one-Lead rule client-side (build.md §7.2);
// the plan-aware bot counter is informational only — AO metering stays the
// control point (no hard max_bots gate, §3.4).

const emptyDraft={
  name: '',
  role: '',
  systemPrompt: '',
  modelTier: 'tier_balanced',
  teamId: '',
  isLead: false,
  consultableBots: []
};

const emptyTeamDraft={
  name: '',
  module: 'headquarters',
  leadName: '',
  leadRole: '',
  members: [{name: '',role: ''},{name: '',role: ''}]
};

export default function Bots() {
  const state=useWorkspace();
  const [selectedId,setSelectedId]=useState(state.bots[0]?.id || null);
  const [source,setSource]=useState('platform');
  const [pickerMode,setPickerMode]=useState('bots'); // 'bots' | 'teams'
  const [draft,setDraft]=useState(emptyDraft);
  const [showBotForm,setShowBotForm]=useState(false);
  const [teamDraft,setTeamDraft]=useState(emptyTeamDraft);
  const [showTeamForm,setShowTeamForm]=useState(false);
  const [error,setError]=useState(null);
  const [notice,setNotice]=useState(null);
  const [busy,setBusy]=useState(false);
  const [capDraft,setCapDraft]=useState('');
  const [sandboxOpen,setSandboxOpen]=useState(false);
  const [sandboxText,setSandboxText]=useState('');
  const [sandboxThread,setSandboxThread]=useState([]);
  const [sandboxBusy,setSandboxBusy]=useState(false);
  const [sandboxError,setSandboxError]=useState(null);
  const bot=state.bots.find((item)=> item.id===selectedId);
  const teammates=state.bots.filter((item)=> (
    bot && item.teamId===bot.teamId && item.id!==bot.id
  ));
  const providers=(state.providerCredentials || []).filter((item)=> item.enabled);
  const softLimit=getBotSoftLimit(state);

  const pickers={
    bots: state.bots.map((item)=> (
      <BotCard
        key={item.id}
        bot={item}
        team={state.teams.find((team)=> team.id===item.teamId)}
        selected={item.id===selectedId}
        onClick={()=> {
          setSelectedId(item.id);
          setSource(item.modelSource==='byo' ? 'byo' : 'platform');
        }}
      />
    )),
    teams: state.teams.map((team)=> {
      const roster=state.bots.filter((item)=> item.teamId===team.id);

      return (
        <div key={team.id} className="team-group">
          <button
            type="button"
            className="team-group-head"
            onClick={()=> setPickerMode('bots')}
            title="Back to all bots"
          >
            <b>{team.name}</b>
            <small>{team.module}{team.custom ? ' · custom' : ''} · {roster.length} bots</small>
          </button>
          {roster.map((item)=> (
            <BotCard
              key={item.id}
              bot={item}
              team={team}
              selected={item.id===selectedId}
              onClick={()=> {
                setSelectedId(item.id);
                setSource(item.modelSource==='byo' ? 'byo' : 'platform');
              }}
            />
          ))}
        </div>
      );
    })
  };

  if (!bot) {
    return (
      <div className="page narrow">
        <header className="page-header">
          <span className="eyebrow">Bots</span>
          <h1>Configure your AI organization.</h1>
          <p>No bots are available yet. Create your first specialist below.</p>
        </header>
        <section className="panel">
          <BotCreateForm
            draft={draft}
            setDraftField={setDraftField}
            state={state}
            busy={busy}
            onSubmit={onCreateBot}
          />
          <RosterTransferPanel
            onError={setError}
            onNotice={setNotice}
          />
        </section>
      </div>
    );
  }

  function update(patch) {
    return dataClient.update('bots',bot.id,patch);
  }

  function setModelSource(nextSource) {
    setSource(nextSource);

    if (nextSource==='platform') {
      update({modelSource: 'platform',byoModelId: null});
      return;
    }

    const firstProvider=providers[0];

    update({
      modelSource: 'byo',
      byoModelId: firstProvider?.id || null
    });
  }

  function toggleConsultable(id) {
    const selected=bot.consultableBots.includes(id);

    return update({
      consultableBots: selected
        ? bot.consultableBots.filter((item)=> item!==id)
        : [...bot.consultableBots,id]
    });
  }

  async function onToggleLead(event) {
    const isLead=event.target.checked;

    if (isLead) {
      const existingLead=teammates.find((item)=> item.isLead);

      if (existingLead) {
        setError(
          `"${state.teams.find((team)=> team.id===bot.teamId)?.name || 'This team'}" already has a Lead (${existingLead.name}). Demote ${existingLead.name} first, or add this bot as a specialist.`
        );
        return;
      }
    }

    setError(null);
    await update({isLead});
  }

  async function onClone() {
    setBusy(true);
    setError(null);

    try {
      const clone=await cloneBot(bot.id);
      setSelectedId(clone.id);
    } catch (cloneError) {
      setError(cloneError.message);
    } finally {
      setBusy(false);
    }
  }

  async function onCreateBot(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const created=await createBot({
        name: draft.name,
        role: draft.role,
        systemPrompt: draft.systemPrompt,
        modelTier: draft.modelTier,
        teamId: draft.teamId || null,
        isLead: draft.isLead,
        consultableBots: draft.isLead ? draft.consultableBots : [],
        agentClass: draft.isLead ? 'tooling_advanced' : 'chat_simple'
      });

      setSelectedId(created.id);
      setDraft(emptyDraft);
      setShowBotForm(false);
    } catch (createError) {
      setError(createError.message);
    } finally {
      setBusy(false);
    }
  }

  async function onCreateTeam(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const {lead}=await createCustomTeam({
        name: teamDraft.name,
        module: teamDraft.module,
        leadName: teamDraft.leadName,
        leadRole: teamDraft.leadRole,
        members: teamDraft.members
      });

      setSelectedId(lead.id);
      setTeamDraft(emptyTeamDraft);
      setShowTeamForm(false);
    } catch (teamError) {
      setError(teamError.message);
    } finally {
      setBusy(false);
    }
  }

  function setDraftField(field,value) {
    setDraft((current)=> ({...current,[field]: value}));
  }

  function setTeamDraftField(field,value) {
    setTeamDraft((current)=> ({...current,[field]: value}));
  }

  function setTeamMemberField(index,field,value) {
    setTeamDraft((current)=> ({
      ...current,
      members: current.members.map((member,idx)=> (
        idx===index ? {...member,[field]: value} : member
      ))
    }));
  }

  function addCapability() {
    const value=capDraft.trim().toLowerCase().replace(/\s+/g,'-');

    if (!value) return;

    if ((bot.capabilities || []).includes(value)) {
      setCapDraft('');
      return;
    }

    update({capabilities: [...(bot.capabilities || []),value]});
    setCapDraft('');
  }

  function removeCapability(value) {
    update({capabilities: (bot.capabilities || []).filter((item)=> item!==value)});
  }

  async function onSandboxSend(prompt, files = []) {
    if (!prompt.trim() && !files.length) return;
    if (sandboxBusy) return;

    setSandboxThread((thread)=> [
      ...thread,
      {
        id:`sandbox_u_${Date.now()}`,
        sender:'user',
        text:prompt,
        attachments: files.map((f)=> ({name: f.name,size: `${Math.round(f.size/1024)} KB`}))
      }
    ]);
    setSandboxBusy(true);
    setSandboxError(null);

    try {
      const reply=await sendChat({
        module: bot.module || 'headquarters',
        botId: bot.id,
        text: prompt,
        sandbox: true
      });
      setSandboxThread((thread)=> [...thread,reply]);
    } catch (sendError) {
      setSandboxError(sendError?.message || 'Sandbox test failed.');
    } finally {
      setSandboxBusy(false);
    }
  }

  function resetSandbox() {
    setSandboxThread([]);
    setSandboxError(null);
  }

  const selectedProvider=providers.find((item)=> item.id===bot.byoModelId);

  return (
    <div className="page">
      <header className="page-header">
        <span className="eyebrow">Bots</span>
        <h1>Configure your AI organization.</h1>
        <p>Roles, prompts, models, and delegation stay explicit and reviewable.</p>
        <p className="soft-limit">
          {softLimit.used} of ~{softLimit.limit} bots on {softLimit.planName} — usage is metered in
          Agent Operations, not bot count.
        </p>
      </header>

      {error && (
        <div className="chat-error" role="alert">{error}</div>
      )}

      {notice && (
        <div className="notice-success" role="status">{notice}</div>
      )}

      <div className="settings-grid">
        <aside className="panel bot-picker">
          <div className="picker-toggle">
            <button
              type="button"
              className={pickerMode==='bots' ? 'button primary compact' : 'button secondary compact'}
              onClick={()=> setPickerMode('bots')}
            >
              Bots
            </button>
            <button
              type="button"
              className={pickerMode==='teams' ? 'button primary compact' : 'button secondary compact'}
              onClick={()=> setPickerMode('teams')}
            >
              Teams
            </button>
          </div>
          {pickers[pickerMode]}
          <div className="picker-actions">
            <button
              className="button secondary"
              onClick={()=> {
                setShowBotForm(!showBotForm);
                setShowTeamForm(false);
              }}
            >
              + Create bot
            </button>
            <button
              className="button ghost compact"
              onClick={()=> {
                setShowTeamForm(!showTeamForm);
                setShowBotForm(false);
              }}
            >
              + New team
            </button>
          </div>

          {showBotForm && (
            <BotCreateForm
              draft={draft}
              setDraftField={setDraftField}
              state={state}
              busy={busy}
              onSubmit={onCreateBot}
              compact
            />
          )}

          {showTeamForm && (
            <TeamCreateForm
              draft={teamDraft}
              setTeamDraftField={setTeamDraftField}
              setTeamMemberField={setTeamMemberField}
              state={state}
              busy={busy}
              onSubmit={onCreateTeam}
              compact
            />
          )}

          <RosterTransferPanel
            onError={setError}
            onNotice={setNotice}
          />
        </aside>

        <section className="panel bot-settings">
          <div className="inline spread">
            <div>
              <span className="eyebrow">Bot configuration</span>
              <h2>{bot.name}</h2>
            </div>
            <div className="inline">
              <button
                className="button ghost compact"
                onClick={onClone}
                disabled={busy}
                title="Copy settings and prompt into a new bot"
              >
                Clone
              </button>
              <label className="toggle">
                <input
                  type="checkbox"
                  checked={bot.enabled}
                  onChange={(event)=> update({enabled: event.target.checked})}
                />
                <span />
                Enabled
              </label>
            </div>
          </div>

          <div className="form-grid">
            <label>
              Name
              <input
                value={bot.name}
                onChange={(event)=> update({name: event.target.value})}
              />
            </label>
            <label>
              Role
              <input
                value={bot.role}
                onChange={(event)=> update({role: event.target.value})}
              />
            </label>
            <label className="full">
              System prompt
              <textarea
                value={bot.systemPrompt}
                onChange={(event)=> update({systemPrompt: event.target.value})}
              />
            </label>
          </div>

          <h3>Model source</h3>
          <div className="source-tabs">
            <button
              type="button"
              className={source==='platform' ? 'button primary compact' : 'button secondary compact'}
              onClick={()=> setModelSource('platform')}
            >
              Platform tier
            </button>
            <button
              type="button"
              className={source==='byo' ? 'button primary compact' : 'button secondary compact'}
              onClick={()=> setModelSource('byo')}
            >
              Connected provider
            </button>
          </div>

          {source==='platform' && (
            <ModelTierSelector
              tiers={state.modelTiers}
              value={bot.modelTier}
              onChange={(modelTier)=> update({modelTier,modelSource: 'platform',byoModelId: null})}
            />
          )}

          {source==='byo' && (
            providers.length
              ? <ProviderSelector
                  providers={providers}
                  value={selectedProvider?.id}
                  onChange={(byoModelId)=> update({byoModelId,modelSource: 'byo'})}
                />
              : <p className="empty-copy">
                  Connect and enable a provider in Models before selecting it here.
                </p>
          )}

          <div className="lead-config">
            <label className="check-row">
              <input
                type="checkbox"
                checked={bot.isLead}
                onChange={onToggleLead}
              />
              <span>
                <b>Team Lead</b>
                <small>Can consult selected teammates and synthesize one final response.</small>
              </span>
            </label>

            {bot.isLead && (
              <div>
                <h4>Consultable teammates</h4>
                {teammates.length ? teammates.map((teammate)=> (
                  <label className="check-row" key={teammate.id}>
                    <input
                      type="checkbox"
                      checked={bot.consultableBots.includes(teammate.id)}
                      onChange={()=> toggleConsultable(teammate.id)}
                    />
                    <span>
                      <b>{teammate.name}</b>
                      <small>{teammate.role}</small>
                    </span>
                  </label>
                )) : (
                  <p className="empty-copy">No teammates in this team yet — add specialists first.</p>
                )}
              </div>
            )}
          </div>

          <h3>Scope &amp; capabilities</h3>
          <div className="scope-editor">
            <label className="full">
              Scope — what this bot is set up to help with
              <textarea
                value={bot.knowledgeScope || bot.knowledge_scope || ''}
                onChange={(event)=> update({knowledgeScope: event.target.value})}
                placeholder="Describe the domain this bot should stay inside, e.g. “Questions about this business financial figures: sales, pricing, forecasts, budgets…”"
              />
            </label>
            <div>
              <h4>Capabilities</h4>
              <div className="tag-chips">
                {(bot.capabilities || []).map((capability)=> (
                  <span className="chip" key={capability}>
                    {capability}
                    <button
                      type="button"
                      className="chip-remove"
                      onClick={()=> removeCapability(capability)}
                      title={`Remove ${capability}`}
                      aria-label={`Remove ${capability}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
                {!(bot.capabilities || []).length && (
                  <small className="muted">No capabilities declared yet.</small>
                )}
              </div>
              <div className="inline">
                <input
                  value={capDraft}
                  onChange={(event)=> setCapDraft(event.target.value)}
                  onKeyDown={(event)=> {
                    if (event.key==='Enter') {
                      event.preventDefault();
                      addCapability();
                    }
                  }}
                  placeholder="e.g. revenue-analysis"
                />
                <button
                  type="button"
                  className="button secondary compact"
                  onClick={addCapability}
                  disabled={!capDraft.trim()}
                >
                  Add
                </button>
              </div>
            </div>
            <div>
              <h4>Tools</h4>
              <div className="tag-chips">
                {(bot.tools || []).length ? bot.tools.map((tool)=> (
                  <span className="chip read-only" key={tool}>{tool}</span>
                )) : (
                  <small className="muted">No tools assigned.</small>
                )}
              </div>
              <p className="hint-copy">Tool execution is not enabled in this build.</p>
            </div>
          </div>

          <div className="sandbox-block">
            <button
              type="button"
              className="sandbox-toggle"
              onClick={()=> setSandboxOpen(!sandboxOpen)}
              aria-expanded={sandboxOpen}
            >
              <b>Sandbox test chat</b>
              <small>Try this bot’s configured scope + model before trusting it live.</small>
              <span>{sandboxOpen ? '−' : '+'}</span>
            </button>
            {sandboxOpen && (
              <div className="sandbox-chat">
                <div className="messages compact">
                  {!sandboxThread.length && (
                    <p className="muted">
                      No test messages yet. Ask something in scope to see the bot respond — or something outside it to see the scope decline.
                    </p>
                  )}
                  {sandboxThread.map((message)=> (
                    <div key={message.id} className={`message-row ${message.sender}`}>
                      <div className="message-bubble">
                        <p>{message.text}</p>
                        {message.declinedScope && (
                          <small className="scope-decline-chip">Declined — outside this bot’s scope</small>
                        )}
                      </div>
                    </div>
                  ))}
                  {sandboxError && (
                    <div className="chat-error" role="alert">{sandboxError}</div>
                  )}
                </div>
                <ChatInput
                  lead={bot}
                  botOptions={[bot]}
                  botId={bot.id}
                  onSubmit={(prompt, files)=> onSandboxSend(prompt, files)}
                  busy={sandboxBusy}
                  placeholder={`Ask ${bot.name} anything…`}
                  compact
                />
                <p className="hint-copy">
                  Sandbox replies are never stored, and a scope decline costs 0 AO. Successful model calls count against your Agent Operations like a real message.
                </p>
                {sandboxThread.length > 0 && (
                  <button type="button" className="button ghost compact" onClick={resetSandbox}>
                    Clear test chat
                  </button>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

function BotCreateForm({draft,setDraftField,state,busy,onSubmit,compact=false}) {
  const teamHasLead=Boolean(
    draft.teamId
    && state.bots.some((item)=> item.teamId===draft.teamId && item.isLead)
  );

  return (
    <form className="picker-form" onSubmit={onSubmit}>
      <label>
        Name
        <input
          value={draft.name}
          onChange={(event)=> setDraftField('name',event.target.value)}
          placeholder="e.g. Vendor Negotiator"
          required
        />
      </label>
      <label>
        Role
        <input
          value={draft.role}
          onChange={(event)=> setDraftField('role',event.target.value)}
          placeholder="e.g. Procurement specialist"
        />
      </label>
      <label>
        System prompt
        <textarea
          value={draft.systemPrompt}
          onChange={(event)=> setDraftField('systemPrompt',event.target.value)}
          placeholder="What this bot should focus on and how it should respond."
        />
      </label>
      <label>
        Team
        <select
          value={draft.teamId}
          onChange={(event)=> {
            setDraftField('teamId',event.target.value);
            if (event.target.value==='') setDraftField('isLead',false);
          }}
        >
          <option value="">No team (lone specialist)</option>
          {state.teams.map((team)=> (
            <option key={team.id} value={team.id}>{team.name}</option>
          ))}
        </select>
      </label>
      <label>
        Model tier
        <select
          value={draft.modelTier}
          onChange={(event)=> setDraftField('modelTier',event.target.value)}
        >
          {state.modelTiers.map((tier)=> (
            <option key={tier.id} value={tier.id}>
              {tier.name} · {tier.speed} · {tier.cost}
            </option>
          ))}
        </select>
      </label>
      <label className="check-row">
        <input
          type="checkbox"
          checked={draft.isLead}
          disabled={teamHasLead || !draft.teamId}
          onChange={(event)=> setDraftField('isLead',event.target.checked)}
        />
        <span>
          <b>Team Lead</b>
          <small>Needs a team; disabled when it already has a Lead.</small>
        </span>
      </label>
      {draft.isLead && (
        <div className="lead-picks">
          <h4>Consultable teammates (existing, in this team)</h4>
          {state.bots
            .filter((item)=> item.teamId===draft.teamId)
            .map((item)=> (
              <label className="check-row" key={item.id}>
                <input
                  type="checkbox"
                  checked={draft.consultableBots.includes(item.id)}
                  onChange={()=> setDraftField(
                    'consultableBots',
                    draft.consultableBots.includes(item.id)
                      ? draft.consultableBots.filter((id)=> id!==item.id)
                      : [...draft.consultableBots,item.id]
                  )}
                />
                <span>
                  <b>{item.name}</b>
                  <small>{item.role}</small>
                </span>
              </label>
            ))}
        </div>
      )}
      <button className="button primary" disabled={busy || !draft.name.trim()}>
        Create bot{compact ? '' : ' and open it'}
      </button>
    </form>
  );
}

function TeamCreateForm({draft,setTeamDraftField,setTeamMemberField,state,busy,onSubmit,compact=false}) {
  return (
    <form className="picker-form" onSubmit={onSubmit}>
      <label>
        Team name
        <input
          value={draft.name}
          onChange={(event)=> setTeamDraftField('name',event.target.value)}
          placeholder="e.g. Procurement Team"
          required
        />
      </label>
      <label>
        Module
        <select
          value={draft.module}
          onChange={(event)=> setTeamDraftField('module',event.target.value)}
        >
          {(state.modules || []).map((module)=> (
            <option key={module.slug} value={module.slug}>{module.name}</option>
          ))}
        </select>
      </label>
      <label>
        Lead name
        <input
          value={draft.leadName}
          onChange={(event)=> setTeamDraftField('leadName',event.target.value)}
          placeholder="e.g. Sam"
          required
        />
      </label>
      <label>
        Lead role
        <input
          value={draft.leadRole}
          onChange={(event)=> setTeamDraftField('leadRole',event.target.value)}
          placeholder="e.g. Procurement Lead"
        />
      </label>
      <div className="lead-picks">
        <h4>Specialists (2 recommended)</h4>
        {draft.members.map((member,index)=> (
          <div className="member-row" key={index}>
            <input
              value={member.name}
              onChange={(event)=> setTeamMemberField(index,'name',event.target.value)}
              placeholder={`Specialist ${index + 1} name`}
            />
            <input
              value={member.role}
              onChange={(event)=> setTeamMemberField(index,'role',event.target.value)}
              placeholder="Role (optional)"
            />
          </div>
        ))}
      </div>
      <button className="button primary" disabled={busy || !draft.name.trim() || !draft.leadName.trim()}>
        Create team with Lead{compact ? '' : ' and open it'}
      </button>
    </form>
  );
}
